import { JobOfferPreview } from "@/lib/interfaces/embed";
import { useNavigate } from "react-router-dom";
import useApiRequest from "../../../lib/hooks/useRequest";
import { Card } from "../../../ui/components/ui/card";
import { useSimilarJobsList } from "./use-similar-jobs-list";

export function JobPostsV2SimilarJobs({
  organizationId,
  currentJobId,
  brandColor,
  initialSimilarJobs,
  isDebug,
  sectionClassName,
  disableNavigation,
}: {
  organizationId: string;
  currentJobId: string;
  brandColor: string;
  initialSimilarJobs?: JobOfferPreview[];
  isDebug?: boolean;
  /** When there are no jobs the component returns null, so no empty outer section is left behind. */
  sectionClassName: string;
  /** When true, cards do not navigate (e.g. Karriereportal settings preview). */
  disableNavigation?: boolean;
}) {
  const navigate = useNavigate();
  const { apiRequest } = useApiRequest();

  const { hasJobs, visibleJobs, canLoadMore, isLoadingMore, onLoadMore } = useSimilarJobsList({
    organizationId,
    currentJobId,
    initialSimilarJobs,
    isDebug,
    apiRequest,
  });

  if (!hasJobs) return null;

  return (
    <section className={`embed-job-similar ${sectionClassName}`.trim()}>
      <div className="mx-auto max-w-6xl px-4 py-12 md:py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900">
            <span>Ähnliche </span>
            <span style={{ color: brandColor }}>Stellenangebote</span>
          </h2>
          <p className="mt-3 text-sm md:text-base text-gray-600">
            Diese Positionen könnten auch interessant für dich sein.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleJobs.map((job) => (
            <Card key={job._id} className="embed-job-similar-card p-6 shadow-md transition-shadow hover:shadow-lg">
              <div className="text-sm font-semibold text-gray-900">{job.title}</div>
              <div className="mt-2 text-xs text-gray-600">
                {job.location ? <span>{job.location}</span> : null}
                {job.location && job.hourlySalary ? <span> · </span> : null}
                {job.hourlySalary ? <span style={{ color: brandColor }}>{job.hourlySalary}€/Std.</span> : null}
              </div>

              {disableNavigation ? (
                <span className="mt-5 inline-block text-sm font-semibold text-gray-400 cursor-default">
                  Details ansehen →
                </span>
              ) : (
                <button
                  type="button"
                  className="mt-5 text-sm font-semibold"
                  style={{ color: brandColor }}
                  onClick={() => navigate(`/job-angebote/iframe/v2/${organizationId}/${job._id}`)}
                >
                  Details ansehen →
                </button>
              )}
            </Card>
          ))}
        </div>

        {canLoadMore && !disableNavigation ? (
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              className="text-sm font-semibold"
              style={{ color: brandColor }}
              onClick={onLoadMore}
              disabled={isLoadingMore}
            >
              Alle offenen Stellen ansehen →
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
